import { useMemo } from 'react'
import {
  Banknote,
  Clock,
  Gem,
  Gift,
  Leaf,
  Lock,
  ShieldCheck,
  Sparkles,
  Truck,
  type LucideIcon,
} from 'lucide-react'
import { useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

export interface HomePromoSlide {
  icon: LucideIcon
  label: string
}

export const HOME_PROMO_SLIDES: HomePromoSlide[] = [
  { icon: Truck, label: 'Pan-India home delivery' },
  { icon: Gem, label: 'Premium quality attars' },
  { icon: Leaf, label: 'Alcohol-free attars' },
  { icon: Clock, label: 'Long-lasting fragrance' },
  { icon: ShieldCheck, label: '100% authentic fragrances' },
  { icon: Gift, label: 'Luxury gift packaging' },
  { icon: Sparkles, label: 'Handcrafted with care' },
  { icon: Banknote, label: 'Cash on delivery available' },
  { icon: Lock, label: 'Secure online checkout' },
]

function PromoItem({ icon: Icon, label }: HomePromoSlide) {
  return (
    <span className="home-promo-item inline-flex shrink-0 items-center gap-2.5">
      <span className="home-promo-icon inline-flex h-7 w-7 items-center justify-center rounded-full border border-gold-400/25 bg-gold-400/10">
        <Icon className="h-3.5 w-3.5 text-gold-300" strokeWidth={1.75} aria-hidden />
      </span>
      <span className="font-display text-sm tracking-wide text-gold-100/90 sm:text-[15px]">
        {label}
      </span>
    </span>
  )
}

function HomePromoTickerRow({ reverse = false }: { reverse?: boolean }) {
  const loopItems = useMemo(
    () => [...HOME_PROMO_SLIDES, ...HOME_PROMO_SLIDES],
    []
  )

  return (
    <div className="home-promo-ticker-wrap relative overflow-hidden py-3">
      <div
        className={cn(
          'home-promo-ticker-track flex w-max items-center gap-10 px-6 sm:gap-14 sm:px-10',
          reverse && 'home-promo-ticker-track--reverse'
        )}
      >
        {loopItems.map((slide, index) => (
          <span
            key={`${reverse ? 'rev' : 'fwd'}-${slide.label}-${index}`}
            className="inline-flex shrink-0 items-center gap-10 sm:gap-14"
          >
            <PromoItem {...slide} />
            <span
              className={cn(
                'hidden h-1 w-1 rotate-45 rounded-[1px] bg-gold-400/35 sm:block',
                index === loopItems.length - 1 && 'sm:hidden'
              )}
              aria-hidden
            />
          </span>
        ))}
      </div>
    </div>
  )
}

export function HomePromoTicker() {
  const reduceMotion = useReducedMotion()

  return (
    <section
      aria-label="Store highlights"
      className="home-promo-ticker relative border-b border-gold-500/15 bg-gradient-to-r from-luxury-950 via-luxury-900 to-luxury-950"
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold-400/50 to-transparent"
        aria-hidden
      />
      <div className="home-promo-ticker-shine pointer-events-none absolute inset-0" aria-hidden />

      {reduceMotion ? (
        <ul className="store-container flex flex-wrap items-center justify-center gap-x-6 gap-y-2 py-3">
          {HOME_PROMO_SLIDES.map((slide) => (
            <li key={slide.label}>
              <PromoItem {...slide} />
            </li>
          ))}
        </ul>
      ) : (
        <>
          <HomePromoTickerRow />
          <div
            className="mx-6 h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent sm:mx-10"
            aria-hidden
          />
          <HomePromoTickerRow reverse />
        </>
      )}
    </section>
  )
}
